import { IconType } from 'react-icons';
import {
  HiOutlineBuildingStorefront,
  HiOutlineShoppingCart,
  HiOutlineScale,
  HiOutlineMapPin,
  HiOutlineBanknotes,
  HiOutlineArrowsRightLeft,
  HiOutlineChartBarSquare,
  HiOutlineCog6Tooth,
} from 'react-icons/hi2';
import { SectionKey } from './types';

/** Column kinds drive both cell rendering and the edit control in `MappingTable`. `readonly`
 * columns are computed/derived-elsewhere fields the design intentionally never lets an admin
 * hand-edit (e.g. `cart_estimate.total_stores`), independent of what Hasura's `update_permissions`
 * technically allows — the UI exposes a deliberately narrower edit surface. */
export type ColumnKind = 'readonly' | 'readonlyStrong' | 'readonlyNumber' | 'text' | 'textarea' | 'number' | 'decimal' | 'select' | 'date' | 'boolean';

export interface ColumnDef {
  field: string;
  header: string;
  kind: ColumnKind;
  options?: string[];
  optionLabels?: Record<string, string>;
  minWidth?: number;
}

export interface FilterDef {
  field: string;
  label: string;
  allLabel: string;
  options: string[];
}

export interface SectionMeta {
  key: SectionKey;
  label: string;
  icon: IconType;
  title: string;
  subtitle: string;
  table: string;
  columns: ColumnDef[];
  searchColumns: string[];
  defaultSortField: string;
  addable: boolean;
  deletable: boolean;
  emptyRow: Record<string, unknown>;
  uploadMode: 'update-only' | 'upsert' | 'disabled';
  keyFields?: string[];
  /** Optional equality filters shown in the toolbar (e.g. Channel, Customer Type). */
  extraFilters?: FilterDef[];
}

export const CHANNEL_OPTIONS = [
  'Other',
  'Diy',
  'Specialty Retail',
  'Club',
  'Sporting Goods, Hobby, And Musical Instrument Stores',
  'Convenience Stores',
  'Channel Partners & Other',
  'Grocery',
  'Lawn And Garden Equipment And Supplies Stores',
  'Pharmacies And Drug Stores',
  'Dollar Stores',
  'General Merchandise Stores, Including Warehouse Clubs And Supercenters',
  'Mass Retail',
];
export const CUSTOMER_TYPE_OPTIONS = [
  'Other',
  'Service Provider',
  'Intercompany',
  'Non Store/Corporate Account',
  'Cart Manufacturer',
  'Store',
  'Distributor',
];
export const JURISDICTION_TYPE_OPTIONS = ['CITY', 'COUNTY/DISTRICT', 'STATE'];
export const WHEEL_TYPE_OPTIONS = ['SINGLE', 'PAIRED', 'CASE PACK'];

export const SECTIONS: SectionMeta[] = [
  {
    key: 'customer',
    label: 'Customer Mapping',
    icon: HiOutlineBuildingStorefront,
    title: 'Customer Mapping',
    subtitle: 'Assign each customer account to a sales channel and customer type.',
    table: 'customers',
    searchColumns: ['customer_no', 'brand'],
    defaultSortField: 'customer_no',
    addable: false,
    deletable: false,
    uploadMode: 'update-only',
    keyFields: ['customer_no'],
    emptyRow: {},
    extraFilters: [
      { field: 'channel', label: 'Channel', allLabel: 'All Channels', options: CHANNEL_OPTIONS },
      { field: 'customer_type', label: 'Customer Type', allLabel: 'All Types', options: CUSTOMER_TYPE_OPTIONS },
    ],
    columns: [
      { field: 'customer_no', header: 'Customer No.', kind: 'readonly' },
      { field: 'brand', header: 'Brand', kind: 'readonly' },
      { field: 'channel', header: 'Channel', kind: 'select', options: CHANNEL_OPTIONS },
      { field: 'customer_type', header: 'Customer Type', kind: 'select', options: CUSTOMER_TYPE_OPTIONS },
    ],
  },
  {
    key: 'cart',
    label: 'Cart Estimates',
    icon: HiOutlineShoppingCart,
    title: 'Cart Estimates',
    subtitle: 'Adjust cart count and install estimates per retailer.',
    table: 'cart_estimate',
    searchColumns: ['brand_name', 'ultimate_parent'],
    defaultSortField: 'brand_name',
    addable: false,
    deletable: false,
    uploadMode: 'update-only',
    keyFields: ['brand_name', 'ultimate_parent', 'country_code'],
    emptyRow: {},
    columns: [
      { field: 'brand_name', header: 'Brand', kind: 'readonlyStrong' },
      { field: 'ultimate_parent', header: 'Ultimate Parent', kind: 'readonly' },
      { field: 'country_code', header: 'Country Code', kind: 'readonly' },
      { field: 'total_stores', header: 'Total Stores', kind: 'readonlyNumber' },
      { field: 'total_customers_count', header: 'Total Customers', kind: 'readonlyNumber' },
      { field: 'median_wheels_installed', header: 'Median Wheels', kind: 'readonlyNumber' },
      { field: 'mean_wheels_installed', header: 'Mean Wheels', kind: 'readonlyNumber' },
      { field: 'carts_per_store', header: 'Carts / Store', kind: 'number' },
      { field: 'total_carts', header: 'Total Carts', kind: 'number' },
      { field: 'purchek_install_estimation', header: 'PURCHEK INSTALL EST.', kind: 'number' },
      { field: 'containment_install_estimation', header: 'CONTAINMENT INSTALL EST.', kind: 'number' },
    ],
  },
  {
    key: 'ordinance',
    label: 'Cart Ordinance Data',
    icon: HiOutlineScale,
    title: 'Cart Ordinance Data',
    subtitle: 'Maintain jurisdiction-level shopping cart ordinance records.',
    table: 'cart_ordinance',
    searchColumns: ['jurisdiction_name', 'jurisdiction_type', 'code_number'],
    defaultSortField: 'jurisdiction_name',
    addable: true,
    deletable: true,
    uploadMode: 'upsert',
    keyFields: ['jurisdiction_type', 'jurisdiction_name'],
    emptyRow: {
      jurisdiction_type: '', jurisdiction_name: '', source: '', source_url: '',
      has_penalty: false, penalty_amount: null, summary_points: '', code_number: '', published_on: null,
      country_code: '',
    },
    columns: [
      { field: 'jurisdiction_type', header: 'Jurisdiction Type', kind: 'select', options: JURISDICTION_TYPE_OPTIONS, minWidth: 140 },
      { field: 'jurisdiction_name', header: 'Jurisdiction Name', kind: 'text', minWidth: 170 },
      { field: 'country_code', header: 'Country Code', kind: 'text', minWidth: 120 },
      { field: 'source', header: 'Source', kind: 'text', minWidth: 200 },
      { field: 'source_url', header: 'Source URL', kind: 'textarea', minWidth: 240 },
      { field: 'has_penalty', header: 'Has Penalty', kind: 'boolean', minWidth: 90 },
      { field: 'penalty_amount', header: 'Penalty Amount', kind: 'decimal', minWidth: 130 },
      { field: 'summary_points', header: 'Summary Points', kind: 'textarea', minWidth: 280 },
      { field: 'code_number', header: 'Code Number', kind: 'text', minWidth: 160 },
      { field: 'published_on', header: 'Published', kind: 'date' },
    ],
  },
  {
    key: 'geo',
    label: 'Geo Data',
    icon: HiOutlineMapPin,
    title: 'Geo Data',
    subtitle: 'Maintain zip-to-county-to-state geographic lookups.',
    table: 'geo',
    searchColumns: ['city', 'zipcode', 'county', 'state'],
    defaultSortField: 'city',
    addable: false,
    deletable: false,
    uploadMode: 'update-only',
    keyFields: ['zipcode', 'country_code'],
    emptyRow: {},
    columns: [
      { field: 'zipcode', header: 'Zipcode', kind: 'readonly' },
      { field: 'country_code', header: 'Country Code', kind: 'readonly' },
      { field: 'city', header: 'City', kind: 'text' },
      { field: 'county', header: 'COUNTY/DISTRICT', kind: 'text' },
      { field: 'state', header: 'State', kind: 'text' },
      { field: 'state_code', header: 'State Code', kind: 'text' },
    ],
  },
  {
    key: 'revenue',
    label: 'Revenue Categories',
    icon: HiOutlineBanknotes,
    title: 'Revenue Categories',
    subtitle: 'Map general ledger accounts to revenue categories.',
    table: 'revenue_category',
    searchColumns: ['account_category', 'revenue_account'],
    defaultSortField: 'account_category',
    addable: true,
    deletable: true,
    uploadMode: 'disabled',
    emptyRow: { account_category: '', revenue_account: '' },
    columns: [
      { field: 'account_category', header: 'Account Category', kind: 'text' },
      { field: 'revenue_account', header: 'Revenue Account', kind: 'text' },
    ],
  },
  {
    key: 'brand',
    label: 'Brand Mappings',
    icon: HiOutlineArrowsRightLeft,
    title: 'Brand Mappings',
    subtitle: 'Map Safegraph brand names to Gatekeeper brand names.',
    table: 'gk_sg_brands',
    searchColumns: ['safegraph_brand', 'safegraph_ultimate_parent', 'gatekeeper_brand'],
    defaultSortField: 'safegraph_brand',
    addable: false,
    deletable: false,
    uploadMode: 'update-only',
    keyFields: ['safegraph_brand', 'safegraph_ultimate_parent'],
    emptyRow: {},
    columns: [
      { field: 'safegraph_brand', header: 'Safegraph Brand', kind: 'readonlyStrong' },
      { field: 'safegraph_ultimate_parent', header: 'Ultimate Parent', kind: 'readonly' },
      { field: 'gatekeeper_brand', header: 'Gatekeeper Brand', kind: 'text' },
    ],
  },
  {
    key: 'tam',
    label: 'Total Serviceable Market',
    icon: HiOutlineChartBarSquare,
    title: 'Total Serviceable Market',
    subtitle: 'Review market sizing and assign SAM classification.',
    table: 'sam',
    searchColumns: ['brand_name', 'ultimate_parent'],
    defaultSortField: 'brand_name',
    addable: false,
    deletable: false,
    uploadMode: 'disabled',
    emptyRow: {},
    columns: [
      { field: 'brand_name', header: 'Brand', kind: 'readonlyStrong' },
      { field: 'ultimate_parent', header: 'Ultimate Parent', kind: 'readonly' },
      { field: 'top_category', header: 'Top Category', kind: 'readonly' },
      { field: 'sub_category', header: 'Sub Category', kind: 'readonly' },
      { field: 'no_of_stores', header: 'No. of Stores', kind: 'readonlyNumber' },
      { field: 'channel', header: 'Channel', kind: 'select', options: CHANNEL_OPTIONS },
      { field: 'country', header: 'Country', kind: 'readonly' },
      { field: 'sam_cart', header: 'Cart Market Eligible', kind: 'boolean' },
      { field: 'sam_cv', header: 'CV Market Eligible', kind: 'boolean' },
    ],
  },
  {
    key: 'wheel',
    label: 'Wheel List Mappings',
    icon: HiOutlineCog6Tooth,
    title: 'Wheel List Mappings',
    subtitle: 'Maintain wheel item numbers and wheel types.',
    table: 'wheel',
    searchColumns: ['item_number', 'global_region'],
    defaultSortField: 'item_number',
    addable: false,
    deletable: false,
    uploadMode: 'disabled',
    emptyRow: {},
    columns: [
      { field: 'global_region', header: 'Global Region', kind: 'readonly' },
      { field: 'item_number', header: 'Item Number', kind: 'readonlyStrong' },
      { field: 'pcc', header: 'PCC', kind: 'readonly' },
      { field: 'wheel_type', header: 'Wheel Type', kind: 'select', options: WHEEL_TYPE_OPTIONS },
    ],
  },
];

export const SECTION_BY_KEY: Record<SectionKey, SectionMeta> = Object.fromEntries(SECTIONS.map((s) => [s.key, s])) as Record<SectionKey, SectionMeta>;
